import { useEffect, useState } from "react"
import {
  getReferralsOverviewApi,
  ReferralsOverview,
} from "../../features/referrals/api/referrals.api"
import { LineChart, ChartCard } from "../analytics/Charts"
import {
  CursorArrowRaysIcon,
  UserPlusIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/16/solid"

const rate = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0)

const FunnelStep = ({
  icon, label, value, sub, color, iconBg,
}: {
  icon: React.ReactNode; label: string; value: number; sub?: string; color: string; iconBg: string
}) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4 flex-1 min-w-[160px]">
    <div className="flex items-center gap-2 mb-2">
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>{icon}</span>
      <span className="text-xs text-gray-400">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString("ru")}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
)

export const OverviewPage = () => {
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [data, setData] = useState<ReferralsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await getReferralsOverviewApi({
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      })
      setData(res.data)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [fromDate, toDate]) // eslint-disable-line

  const regRate = data ? rate(data.total_registered, data.total_clicks) : 0
  const actRate = data ? rate(data.total_activated, data.total_registered) : 0

  return (
    <div className="space-y-5">
      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3">
        <span className="text-xs text-gray-400">Период:</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <span className="text-gray-300">—</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {(fromDate || toDate) && (
          <button
            onClick={() => { setFromDate(""); setToDate("") }}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Сбросить
          </button>
        )}
      </div>

      {error ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">Не удалось загрузить статистику</p>
          <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
            Повторить
          </button>
        </div>
      ) : loading || !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl h-24 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Funnel */}
          <div className="flex flex-wrap items-center gap-3">
            <FunnelStep
              icon={<CursorArrowRaysIcon className="w-4 h-4 text-blue-600" />}
              iconBg="bg-blue-100"
              label="Клики по ссылкам"
              value={data.total_clicks}
              color="text-gray-900"
            />
            <ArrowRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
            <FunnelStep
              icon={<UserPlusIcon className="w-4 h-4 text-violet-600" />}
              iconBg="bg-violet-100"
              label="Регистрации"
              value={data.total_registered}
              sub={`${regRate}% от кликов`}
              color="text-violet-600"
            />
            <ArrowRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
            <FunnelStep
              icon={<CheckBadgeIcon className="w-4 h-4 text-emerald-600" />}
              iconBg="bg-emerald-100"
              label="Активации"
              value={data.total_activated}
              sub={`${actRate}% от регистраций`}
              color="text-emerald-600"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <ChartCard title="Клики по дням">
              <LineChart
                data={data.clicks_by_day.map((d) => ({ label: d.date, value: d.count }))}
                color="#3b82f6"
              />
            </ChartCard>
            <ChartCard title="Регистрации по дням">
              <LineChart
                data={data.registered_by_day.map((d) => ({ label: d.date, value: d.count }))}
                color="#8b5cf6"
              />
            </ChartCard>
            <ChartCard title="Активации по дням">
              <LineChart
                data={data.activated_by_day.map((d) => ({ label: d.date, value: d.count }))}
                color="#10b981"
              />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  )
}
